import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetIntegrationEntitiesQuery } from '../../../../../store/integrations/integrations'
import { type RedtailActivityCode } from '../../../../../models/Integration'
import { useGetRedtailDefaultFreeActivitiesQuery } from '../../../../../store/redtail/redtail'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { type MultiSelectOption } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { type RedtailInboundContactSyncSettingsFilters, type RedtailSettingsFormValues } from './model'
import { buildCalendarServiceConfig, parseFreeActivities, recordsToBlockingSelection, type FreeActivityRecord } from './freeActivities'

const toSortedOptions = (list: RedtailActivityCode[] | undefined) =>
  (list ?? []).map((e) => ({ id: e.id.toString(), label: e.name })).sort((a, b) => a.label.localeCompare(b.label))

export const useRedtailContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'redtail',
    direction: 'inbound',
    object: 'contact_operation_hashed',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [contactCategoryOptions, setContactCategoryOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactStatusOptions, setContactStatusOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactTypeOptions, setContactTypeOptions] = useState<MultiSelectOption[] | null>(null)
  const [tierCategoriesSelectOptions, setTierCategoriesSelectOptions] = useState<MultiSelectOption[] | null>(null)

  const { data: contactCategoriesEntity, isFetching: isContactCategoriesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_categories',
  })

  const { data: contactStatusesEntity, isFetching: isContactStatusesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_statuses',
  })

  const { data: contactTypesEntity, isFetching: isContactTypesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_types',
  })

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'redtail')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    if (!contactCategoriesEntity) {
      return
    }
    const { contactCategories } = contactCategoriesEntity.data
    if (!contactCategories) {
      return
    }
    const options = contactCategories.map((e) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactCategoryOptions(options)
    setTierCategoriesSelectOptions(options)
  }, [contactCategoriesEntity])

  useEffect(() => {
    if (!contactStatusesEntity) {
      return
    }
    const { contactStatuses } = contactStatusesEntity.data
    if (!contactStatuses) {
      return
    }
    const options = contactStatuses.map((e) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactStatusOptions(options)
  }, [contactStatusesEntity])

  useEffect(() => {
    if (!contactTypesEntity) {
      return
    }
    const { contactTypes } = contactTypesEntity.data
    if (!contactTypes) {
      return
    }
    const options = contactTypes.map((e: string) => ({ id: e.toLowerCase(), label: e }))
    setContactTypeOptions(options)
  }, [contactTypesEntity])

  const save = async (data: RedtailSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
      if (integration) {
        const filters: RedtailInboundContactSyncSettingsFilters = {
          type: (data.inbound.contact.filters.type ?? []).length > 0 ? data.inbound.contact.filters.type : undefined,
          status_id: (data.inbound.contact.filters.status_id ?? []).length > 0 ? data.inbound.contact.filters.status_id : undefined,
          category_id: (data.inbound.contact.filters.category_id ?? []).length > 0 ? data.inbound.contact.filters.category_id : undefined,
        }
        const tierCategories = (data.inbound.contact.tierCategories ?? []).length > 0 ? data.inbound.contact.tierCategories : null
        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters, tierCategories } } })
        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (!checkpoint?.data) {
        if (active) await createCheckpoint(checkpointRequest)
      } else {
        await updateCheckpoint({ ...checkpointRequest, active })
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync: checkpoint?.data?.active ?? false,
    checkpoint,
    isGetCheckpointFetching,
    userData,
    isSyncDisabled,
    isAccountFetching,
    isGetMeQueryFetching,
    contactCategoryOptions,
    isContactCategoriesFetching,
    contactStatusOptions,
    isContactStatusesFetching,
    contactTypeOptions,
    isContactTypesFetching,
    tierCategoriesSelectOptions,
    isFetching:
      !isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching || isContactCategoriesFetching || isContactTypesFetching || isContactStatusesFetching),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
    saveAutoSync,
  }
}

export const useRedtailCalendarSync = ({ withFreeActivities = true }: { withFreeActivities?: boolean } = {}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const {
    data: activityCodesData,
    isFetching: isActivityCodesFetching,
    isError: isActivityCodesError,
  } = useGetIntegrationEntitiesQuery({ accountId, connector: IntegrationServiceId.REDTAIL, entityType: 'activity_codes' }, { skip: !withFreeActivities })
  const {
    data: categoriesData,
    isFetching: isCategoriesFetching,
    isError: isCategoriesError,
  } = useGetIntegrationEntitiesQuery({ accountId, connector: IntegrationServiceId.REDTAIL, entityType: 'categories' }, { skip: !withFreeActivities })

  const {
    data: defaultFreeActivitiesData,
    isFetching: isDefaultFreeActivitiesFetching,
    isError: isDefaultFreeActivitiesError,
  } = useGetRedtailDefaultFreeActivitiesQuery({ accountId }, { skip: !withFreeActivities })
  const defaultFreeActivities = useMemo(() => defaultFreeActivitiesData?.data ?? [], [defaultFreeActivitiesData])
  const defaultsLoaded = withFreeActivities && !isDefaultFreeActivitiesFetching && !isDefaultFreeActivitiesError
  const hasDefaultsError = withFreeActivities && isDefaultFreeActivitiesError

  const activityCodeOptions = useMemo(() => toSortedOptions(activityCodesData?.data?.activityCodes), [activityCodesData])
  const categoryOptions = useMemo(() => toSortedOptions(categoriesData?.data?.categories), [categoriesData])
  const activityCodeIds = useMemo(() => activityCodeOptions.map((o) => o.id), [activityCodeOptions])
  const categoryIds = useMemo(() => categoryOptions.map((o) => o.id), [categoryOptions])

  const defaultSelection = useMemo(
    () => recordsToBlockingSelection(defaultFreeActivities, activityCodeIds, categoryIds),
    [defaultFreeActivities, activityCodeIds, categoryIds],
  )

  const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
  const rawFreeActivities = integration?.serviceConfig?.inbound?.calendar?.freeActivities
  const records = useMemo(() => parseFreeActivities(rawFreeActivities), [rawFreeActivities])
  const hasEntityError = isActivityCodesError || isCategoriesError
  const listsTrusted = !hasEntityError && activityCodeOptions.length > 0 && categoryOptions.length > 0

  const selection = useMemo(() => recordsToBlockingSelection(records, activityCodeIds, categoryIds), [records, activityCodeIds, categoryIds])

  const save = async ({ enabled, freeActivities }: { enabled: boolean; freeActivities: FreeActivityRecord[] | null }) => {
    try {
      setIsUpdating(true)
      if (!integration) {
        return
      }
      const serviceConfig = buildCalendarServiceConfig(integration.serviceConfig, enabled, freeActivities)
      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: { serviceConfig },
      })
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const autoSync = integration?.serviceConfig?.inbound?.calendar?.enabled ?? false

  return {
    userData,
    autoSync,
    activityCodeOptions,
    categoryOptions,
    categoryIds,
    selection,
    records,
    rawFreeActivities,
    defaultSelection,
    defaultsLoaded,
    hasDefaultsError,
    isDefaultFreeActivitiesFetching,
    listsTrusted,
    isFetching: !isUpdating && (isGetMeQueryFetching || isActivityCodesFetching || isCategoriesFetching),
    isSaving: isUpdateUserIntegrationLoading,
    save,
  }
}
